import { faCreditCard } from '@fortawesome/pro-solid-svg-icons';
import { CheckoutElementsProvider } from '@stripe/react-stripe-js/checkout';
import { StripeCheckoutElementsSdkOptions } from '@stripe/stripe-js';
import Button from 'components/ui/Button/Button';
import { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { Col, Row } from 'reactstrap';
import { initializeCheckoutSession, selectCheckoutSession } from 'store/reducers/cartSlice';
import { selectLang } from 'store/reducers/shopSlice';
import { getStripe } from 'utils/stripe';
import AsideSection from '../AsideSection';
import Total from '../Total';
import PaymentForm from './PaymentForm';


type PaymentStepProps = {
    onBack: () => void;
    onPaid: () => void;
};

const PaymentStep = ({ onBack, onPaid }: PaymentStepProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { clientSecret, stripeAccountId, loading, error } = useAppSelector(selectCheckoutSession);
    const language = useAppSelector(selectLang);
    const [busy, setBusy] = useState(false);
    const [paymentCompleted, setPaymentCompleted] = useState(false);

    useEffect(() => {
        dispatch(initializeCheckoutSession());
    }, [dispatch]);

    const options: StripeCheckoutElementsSdkOptions | undefined = clientSecret
        ? {
              clientSecret,
              elementsOptions: {
                  appearance: { theme: 'stripe' },
              },
          }
        : undefined;

    return (
        <Row className="f3-400 justify-content-center">
            <Col lg="6">
                <AsideSection header={t('payment_title')} headerIcon={faCreditCard}>
                    <PaymentTotal className="mb-4" />
                    {loading && <Skeleton height={280} />}
                    {!loading && error === 'config' && <Alert variant="danger">{t('payment_notAvailable')}</Alert>}
                    {!loading && error === 'intent' && (
                        <>
                            <Alert variant="danger">{t('payment_initFailed')}</Alert>
                            <Button
                                type="button"
                                fullwidth
                                pill
                                className="mt-2"
                                onClick={() => dispatch(initializeCheckoutSession())}
                            >
                                {t('payment_tryAgain')}
                            </Button>
                        </>
                    )}

                    {!loading && !error && options && stripeAccountId && (
                        <CheckoutElementsProvider
                            key={clientSecret}
                            stripe={getStripe(stripeAccountId, language)}
                            options={options}
                        >
                            <PaymentForm
                                onPaid={onPaid}
                                onBusyChange={setBusy}
                                onPaymentCompleted={() => setPaymentCompleted(true)}
                            />
                        </CheckoutElementsProvider>
                    )}
                    {!paymentCompleted && (
                        <Button type="button" fullwidth pill className="mt-3" onClick={onBack} disabled={busy}>
                            {t('payment_backToCart')}
                        </Button>
                    )}
                </AsideSection>
            </Col>
        </Row>
    );
};

const PaymentTotal = styled(Total)`
    && {
        background-color: transparent;
    }
`;

export default PaymentStep;
